"use client";

import { useState } from "react";
import { Camera } from "@/components/icons";
import {
  getProductTypeBySlug,
  type ProductType,
} from "@/lib/generated/product-types";
import { Button } from "@/components/ui/button";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { trackToolUsed } from "@/lib/analytics/tool-events";
import { PhotoUploadSection } from "./photo-upload-section";
import { PhotoAngleGuide, PhotoAngleList } from "./photo-angle-guide";
import {
  CalculatorSection,
  CalculatorWorkbench,
} from "@/components/calculators/shared/calculator-workbench";
import { ProductTypeHintSelector } from "./product-type-hint-selector";
import { AnalysisLoadingState } from "./analysis-loading-state";
import { GradingOutcome } from "./grading-outcome";
import { GRADING_INTRO, gradingHeadline } from "./grading-copy";
import {
  useGradingSession,
  type GradingSessionState,
} from "./use-grading-session";
import type { GradingPhoto } from "./grading-session";

interface GradingToolProps {
  productType?: ProductType;
  sourcePath: string;
  showHeading: boolean;
}

function useGradingDraft() {
  const [photos, setPhotos] = useState<Array<GradingPhoto>>([]);
  const [hintSlug, setHintSlug] = useState<string | null>(null);
  const clear = () => {
    setPhotos([]);
    setHintSlug(null);
  };
  return { photos, setPhotos, hintSlug, setHintSlug, clear };
}

type GradingDraft = ReturnType<typeof useGradingDraft>;

function useGradingRun(productType: ProductType | undefined, sourcePath: string) {
  const draft = useGradingDraft();
  const grading = useGradingSession();
  const gradedType = productType ?? getProductTypeBySlug(draft.hintSlug ?? "");
  const previews = draft.photos.map((photo) => photo.previewUrl);
  const analyze = () => {
    trackToolUsed({ tool: "grade", tool_step: "computed", productTypeSlug: gradedType?.slug });
    grading.start({
      photoStorageIds: draft.photos.map((photo) => photo.storageId),
      userProductTypeSlug: gradedType?.slug,
      sourcePath,
    });
  };
  return { draft, grading, gradedType, previews, analyze };
}

export function GradingTool({ productType, sourcePath, showHeading }: GradingToolProps) {
  const { draft, grading, gradedType, previews, analyze } = useGradingRun(productType, sourcePath);

  if (grading.phase === "results" || grading.phase === "error") {
    return (
      <GradingOutcome
        grading={grading}
        productType={productType}
        imagePreviews={previews}
        onReset={() => {
          grading.reset();
          draft.clear();
        }}
      />
    );
  }

  if (!showHeading && grading.phase !== "analyzing") {
    return (
      <WorkbenchUploadStage
        productType={productType}
        draft={draft}
        grading={grading}
        onAnalyze={analyze}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {showHeading && <GradingHeader productType={productType} />}
      {grading.phase === "analyzing" ? (
        <AnalysisLoadingState
          imagePreviews={previews}
          productTypeLabel={gradedType?.pluralDisplayName}
        />
      ) : (
        <UploadStage
          productType={productType}
          draft={draft}
          grading={grading}
          onAnalyze={analyze}
        />
      )}
    </div>
  );
}

function GradingHeader({ productType }: { productType?: ProductType }) {
  return (
    <div className="space-y-3 text-center">
      <h1 className="text-3xl font-bold tracking-tight">
        {gradingHeadline(productType)}
      </h1>
      <p className="text-lg text-muted-foreground">{GRADING_INTRO}</p>
    </div>
  );
}

interface UploadStageProps {
  productType?: ProductType;
  draft: GradingDraft;
  grading: GradingSessionState;
  onAnalyze: () => void;
}

function UploadStage({
  productType,
  draft,
  grading,
  onAnalyze,
}: UploadStageProps) {
  return (
    <div className="space-y-6">
      {!productType && (
        <ProductTypeHintSelector
          selectedSlug={draft.hintSlug}
          onSelect={draft.setHintSlug}
        />
      )}
      <PhotoAngleGuide />
      <PhotoUploadSection
        photos={draft.photos}
        onPhotosChange={draft.setPhotos}
      />
      <ActionErrorAlert
        error={grading.startError}
        onDismiss={grading.dismissStartError}
        reportContext={{ titlePrefix: "Start grading analysis failed" }}
      />
      <AnalyzeButton photoCount={draft.photos.length} onAnalyze={onAnalyze} />
    </div>
  );
}

function WorkbenchUploadStage({
  productType,
  draft,
  grading,
  onAnalyze,
}: UploadStageProps) {
  return (
    <CalculatorWorkbench
      resultsLabel="Before you upload"
      inputs={
        <>
          {!productType && (
            <CalculatorSection
              title="Product"
            >
              <ProductTypeHintSelector
                selectedSlug={draft.hintSlug}
                onSelect={draft.setHintSlug}
                showLabel={false}
              />
            </CalculatorSection>
          )}
          <CalculatorSection title="Photos">
            <PhotoUploadSection
              photos={draft.photos}
              onPhotosChange={draft.setPhotos}
            />
            <ActionErrorAlert
              error={grading.startError}
              onDismiss={grading.dismissStartError}
              reportContext={{ titlePrefix: "Start grading analysis failed" }}
            />
          </CalculatorSection>
        </>
      }
      results={
        <div className="space-y-6">
          <PhotoAngleList />
          <AnalyzeButton
            photoCount={draft.photos.length}
            onAnalyze={onAnalyze}
            fullWidth
          />
        </div>
      }
    />
  );
}

function AnalyzeButton({
  photoCount,
  onAnalyze,
  fullWidth = false,
}: {
  photoCount: number;
  onAnalyze: () => void;
  fullWidth?: boolean;
}) {
  const label =
    photoCount === 0
      ? "Upload photos to analyze"
      : `Analyze ${photoCount} photo${photoCount === 1 ? "" : "s"}`;
  return (
    <div className={fullWidth ? undefined : "flex justify-center"}>
      <Button
        variant="primary"
        onClick={onAnalyze}
        disabled={photoCount === 0}
        className={fullWidth ? "w-full" : undefined}
      >
        <Camera className="h-5 w-5" />
        {label}
      </Button>
    </div>
  );
}
